// Org / membership Firestore helpers
//
// Schema:
//   /orgs/{orgId}                          — org metadata, seat limits, billing info
//   /orgs/{orgId}/members/{encodedEmail}   — per-member doc (roles, uid, status)
//   /orgIndex/{encodedEmail}               — top-level lookup: email → { orgId, roles }
//                                            written on every member add/update so sign-in
//                                            is a single doc read with no collection query

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { firestoreDb } from '../config/firebase';
import { authHeaders } from '../ai/authedFetch';

const db = firestoreDb;

// llm-proxy base URL (strip the /api/chat suffix). The backend is the ONLY writer of
// /userOrgs (Firestore rules forbid client writes), so membership claim/sync go through it.
const PROXY_BASE = (process.env.REACT_APP_GPT_API || 'http://localhost:8020/api/chat').replace(/\/api\/chat$/, '');

// /userOrgs is backend-owned (rules forbid client writes); these go through llm-proxy.
export async function postMembership(path, body) {
  const headers = { 'Content-Type': 'application/json', ...(await authHeaders()) };
  const resp = await fetch(`${PROXY_BASE}${path}`, { method: 'POST', headers, body: JSON.stringify(body || {}) });
  if (!resp.ok) {
    const raw = await resp.text().catch(() => '');
    let serverMsg = '';
    try { serverMsg = JSON.parse(raw)?.error || ''; } catch (_) { /* non-JSON body */ }
    const err = new Error(serverMsg || `Request failed (${resp.status})`);
    err.status = resp.status;
    err.path = path;
    throw err;
  }
  return resp.json();
}

// Encode email to a safe Firestore document ID
export function encodeEmail(email) {
  return encodeURIComponent(email.toLowerCase()).slice(0, 500);
}

// ── Org CRUD ──────────────────────────────────────────────────────────────────

/**
 * Create a new org document. Called after Stripe checkout completes.
 * @param {string} orgId - typically the Stripe customer ID or a generated UUID
 * @param {{ name, stripeCustomerId, stripeSubscriptionId, billingCycle, adminEmail }} data
 */
export async function createOrg(orgId, data) {
  if (!db) return;
  const now = serverTimestamp();
  const plan = data.plan || 'pilot'; // new orgs default to pilot
  await setDoc(doc(db, 'orgs', orgId), {
    orgId,
    name:                   data.name || '',
    plan,                                                    // 'pilot' | 'paid'
    stripeCustomerId:       data.stripeCustomerId || '',
    stripeSubscriptionId:   data.stripeSubscriptionId || '',
    billingCycle:           data.billingCycle || 'monthly',  // 'monthly' | 'annual'
    monthlyRate:            25000,
    annualRate:             240000,
    seatLimit:              data.seatLimit || (plan === 'paid' ? 25 : 3),
    seatsUsed:              0,
    status:                 'active',  // 'active' | 'suspended'
    rateLockedUntil:        data.rateLockedUntil || null,    // Timestamp — set to 1yr from enrollment
    pilotExpiresAt:         plan === 'pilot' ? (data.pilotExpiresAt || defaultPilotExpiry()) : null,
    createdAt:              now,
    updatedAt:              now,
  });
}

// Pilot plans default to 30 days from creation. Admins can override via createOrg data or updateOrg.
export function defaultPilotExpiry() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d;
}

export async function getOrg(orgId) {
  if (!db || !orgId) return null;
  const snap = await getDoc(doc(db, 'orgs', orgId));
  return snap.exists() ? snap.data() : null;
}

/**
 * List all orgs (superadmin only). Returns org metadata for each org.
 */
export async function listAllOrgs() {
  if (!db) return [];
  const snap = await getDocs(collection(db, 'orgs'));
  return snap.docs.map(d => ({ ...d.data(), orgId: d.id }));
}

export async function updateOrg(orgId, fields) {
  if (!db || !orgId) return;
  await updateDoc(doc(db, 'orgs', orgId), { ...fields, updatedAt: serverTimestamp() });
}

// ── Superadmin provisioning (backend-owned; Admin SDK writes org + auth user) ──

/** Create an org and activate its initial members. Superadmin only.
 * dataPooling is the Discovery Cloud consent decision (pooled vs unpooled tier),
 * recorded once at provisioning — omitted/false = unpooled (fail-safe). */
export async function provisionOrg({ orgId, name, plan, seatLimit, members, dataPooling = false }) {
  return postMembership('/admin/provision-org', { orgId, name, plan, seatLimit, members, dataPooling: dataPooling === true });
}

/** Flip an org's plan (pilot <-> paid). Superadmin only. */
export async function setOrgPlan(orgId, plan) {
  return postMembership('/admin/set-plan', { orgId, plan });
}

/** Reversible off-switch. Superadmin only. */
export async function deactivateOrg(orgId) { return postMembership('/admin/deactivate-org', { orgId }); }
/** Reverse of deactivate. Superadmin only. */
export async function reactivateOrg(orgId) { return postMembership('/admin/reactivate-org', { orgId }); }
/** Remove people + archive data (6-mo retention). Superadmin only. */
export async function deleteOrg(orgId) { return postMembership('/admin/delete-org', { orgId }); }
/** Restore an archived org (re-provisions members). Superadmin only. Returns { members:[{email,roles,setPasswordLink}] }. */
export async function restoreOrg(orgId) { return postMembership('/admin/restore-org', { orgId }); }

// ── Member CRUD ───────────────────────────────────────────────────────────────

/**
 * Add or update a member in an org, with immediate activation. Routes through the
 * backend (Admin SDK) so the member's Firebase Auth identity is created and
 * /userOrgs is written — the client cannot do those. Returns a set-password link
 * to hand to the new member.
 * Roles: any combination of 'admin' | 'billing' | 'it' | 'seat'.
 */
export async function addMember(orgId, { email, roles = [] }) {
  if (!orgId || !email) return null;
  return postMembership('/membership/add', { orgId, email, roles });
}

/**
 * Update a member's roles. Routed through the backend (Admin SDK) so /orgIndex and
 * /userOrgs — which the client cannot write — are kept in sync and the seat count is
 * recalculated. Authorized for a superadmin or an admin of the org.
 */
export async function updateMemberRoles(orgId, email, newRoles) {
  if (!orgId || !email) return;
  return postMembership('/membership/update-roles', { orgId, email, roles: newRoles });
}

/**
 * Remove a member from an org. Routed through the backend (Admin SDK): deletes the
 * member doc + /orgIndex + /userOrgs, revokes the member's refresh tokens, and frees
 * their seat. Authorized for a superadmin or an admin of the org.
 */
export async function removeMember(orgId, email) {
  if (!orgId || !email) return;
  return postMembership('/membership/remove', { orgId, email });
}

// M13: Firestore orderBy excludes docs missing the ordered field, so legacy member docs
// (created before the backend wrote addedAt) vanished from the admin list while still
// holding seats. Sort client-side instead; missing addedAt = oldest, so legacy sorts first.
function _addedAtMillis(m) {
  const v = m?.addedAt;
  if (v == null) return -Infinity;
  if (typeof v === 'number') return v;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (v instanceof Date) return v.getTime();
  return -Infinity;
}

export function _sortMembersByAddedAt(members) {
  return [...(members || [])].sort((a, b) => _addedAtMillis(a) - _addedAtMillis(b));
}

/**
 * List all members of an org.
 */
export async function getOrgMembers(orgId) {
  if (!db || !orgId) return [];
  const snap = await getDocs(collection(db, 'orgs', orgId, 'members'));
  return _sortMembersByAddedAt(snap.docs.map(d => d.data()));
}

// ── Sign-in lookup ────────────────────────────────────────────────────────────

/**
 * Pre-auth check — does this email exist in any org?
 * Safe to call without authentication (orgIndex is publicly readable).
 */
export async function isEmailProvisioned(email) {
  if (!db || !email) return false;
  const snap = await getDoc(doc(db, 'orgIndex', encodeEmail(email)));
  return snap.exists();
}

/**
 * Called right after onAuthStateChanged resolves.
 * Returns { orgId, roles } from the public /orgIndex lookup, or null if not provisioned.
 * Does not fetch /orgs/{orgId} — that read requires /userOrgs/{uid} to exist, so the
 * caller must invoke claimMembership first, then getOrg.
 */
export async function lookupMembership(email) {
  if (!db || !email) return null;
  const key  = encodeEmail(email);
  const snap = await getDoc(doc(db, 'orgIndex', key));
  if (!snap.exists()) return null;

  const { orgId, roles } = snap.data();
  return { orgId, roles };
}

/**
 * Claim the signed-in user's membership on first sign-in. Calls the llm-proxy
 * /membership/claim endpoint which — via Admin SDK — records the uid + active status
 * on the member doc and writes the trusted /userOrgs reverse-index. The client can no
 * longer write /userOrgs directly (Firestore rules forbid it); that is what prevents a
 * user from forging membership of an org they were never provisioned into. The server
 * derives the org from the verified token's email, so the args below are retained only
 * for call-site compatibility and are NOT trusted by the server.
 */
export async function claimMembership(orgId, email, uid) { // eslint-disable-line no-unused-vars
  const headers = { 'Content-Type': 'application/json', ...(await authHeaders()) };
  const resp = await fetch(`${PROXY_BASE}/membership/claim`, { method: 'POST', headers });
  if (!resp.ok) {
    const msg = await resp.text().catch(() => '');
    throw new Error(`membership claim failed (${resp.status}): ${msg || resp.statusText}`);
  }
  return resp.json();
}

// ── Domain allowlist ──────────────────────────────────────────────────────────

export async function saveAllowedDomains(orgId, domains) {
  if (!db || !orgId) return;
  await updateDoc(doc(db, 'orgs', orgId), {
    allowedDomains: domains,
    updatedAt:      serverTimestamp(),
  });
}

// ── API tokens ────────────────────────────────────────────────────────────────
// /orgs/{orgId}/tokens/{tokenId}
// The full token value is only returned at creation and never stored in plain text —
// we store a preview (first 10 chars) and a SHA-256 hash for server-side verification.

function generateTokenValue() {
  // 32 random bytes → hex string → cs_ prefix
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return 'cs_' + Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
}

async function sha256(str) {
  const buf  = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate a new API token for the org.
 * Returns { tokenId, tokenValue } — tokenValue is shown ONCE and never stored plain.
 */
export async function generateToken(orgId, { name, createdBy }) {
  if (!db || !orgId) throw new Error('Not ready');
  const tokenValue = generateTokenValue();
  const hash       = await sha256(tokenValue);
  const ref        = doc(collection(db, 'orgs', orgId, 'tokens'));
  await setDoc(ref, {
    tokenId:      ref.id,
    name:         name || 'API Token',
    hash,
    preview:      tokenValue.slice(0, 14) + '…',
    createdBy:    createdBy || '',
    createdAt:    serverTimestamp(),
    lastUsed:     null,
    status:       'active',
  });
  return { tokenId: ref.id, tokenValue };
}

export async function getTokens(orgId) {
  if (!db || !orgId) return [];
  const snap = await getDocs(query(collection(db, 'orgs', orgId, 'tokens'), orderBy('createdAt', 'desc')));
  return snap.docs.map(d => d.data());
}

export async function revokeToken(orgId, tokenId) {
  if (!db || !orgId || !tokenId) return;
  await deleteDoc(doc(db, 'orgs', orgId, 'tokens', tokenId));
}
